'use client';
import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Users,
  Zap,
  Star,
  ArrowUpRight,
} from 'lucide-react';

// The right side: A beautiful bento-style showcase of the platform
function PlatformShowcase() {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Parallax effect on mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    setMousePos({ x, y });
  };

  const getTransform = (depth: number) => {
    return `translate(${mousePos.x * depth * 20}px, ${mousePos.y * depth * 20}px)`;
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setMousePos({ x: 0, y: 0 })}
      className="relative w-full h-full min-h-[400px] md:min-h-[600px] lg:min-h-[700px]"
    >
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-60 blur-3xl pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(147, 51, 234, 0.15) 0%, rgba(192, 132, 252, 0.05) 50%, transparent 70%)',
        }}
      />

      {/* Main deal card - Center piece */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[340px] transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        style={{ transform: `translate(-50%, -50%) ${getTransform(0.5)}`, transitionDelay: '200ms' }}
      >
        <div className="bg-white rounded-2xl shadow-2xl shadow-purple-500/10 border border-gray-100 overflow-hidden">
          {/* Card header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Active Startup</p>
                <p className="text-xs text-gray-500">Series A • Fintech</p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
              Live
            </span>
          </div>
          {/* Card content */}
          <div className="p-5">
            <h3 className="font-display font-semibold text-gray-900 text-lg mb-1">
              TechVenture AI
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              AI-powered financial planning for SMBs
            </p>
            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-gray-500">Funding Progress</span>
                <span className="font-semibold text-gray-900">$1.8M / $2.5M</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full transition-all duration-1000"
                  style={{ width: visible ? '72%' : '0%' }}
                />
              </div>
            </div>
            {/* Participants */}
            <div className="flex items-center justify-between">
              <div className="flex -space-x-2">
                {['bg-purple-500', 'bg-amber-500', 'bg-violet-500', 'bg-fuchsia-500'].map((color, i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 rounded-full ${color} border-2 border-white flex items-center justify-center text-white text-xs font-bold`}
                  >
                    {['F', 'I', 'R', 'A'][i]}
                  </div>
                ))}
              </div>
              <span className="text-xs text-gray-500">4 participants</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top left - Stats card */}
      <div
        className={`absolute top-[8%] left-[5%] w-[140px] sm:w-[180px] transition-all duration-700 hidden md:block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        style={{ transform: getTransform(1.2), transitionDelay: '400ms' }}
      >
        <div className="bg-white rounded-xl shadow-lg shadow-gray-200/50 border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500">This Month</span>
          </div>
          <p className="text-2xl font-display font-bold text-gray-900">$4.2M</p>
          <p className="text-xs text-green-600 font-medium">+23% vs last month</p>
        </div>
      </div>

      {/* Top right - Match notification */}
      <div
        className={`absolute top-[5%] right-[8%] w-[180px] sm:w-[220px] transition-all duration-700 hidden md:block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        style={{ transform: getTransform(0.8), transitionDelay: '500ms' }}
      >
        <div className="bg-white rounded-xl shadow-lg shadow-gray-200/50 border border-gray-100 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">New Match</p>
              <p className="text-xs text-gray-500">Expert matched to deal</p>
              <p className="text-xs text-purple-600 font-medium mt-1">View details →</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom left - Expert card */}
      <div
        className={`absolute bottom-[15%] left-[0%] w-[160px] sm:w-[200px] transition-all duration-700 hidden md:block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        style={{ transform: getTransform(1), transitionDelay: '600ms' }}
      >
        <div className="bg-white rounded-xl shadow-lg shadow-gray-200/50 border border-gray-100 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold">
              RH
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Anonymous</p>
              <p className="text-xs text-gray-500">Ex-McKinsey Partner</p>
            </div>
          </div>
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            ))}
            <span className="text-xs text-gray-500 ml-1">5.0</span>
          </div>
          <div className="flex flex-wrap gap-1">
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">Strategy</span>
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">GTM</span>
          </div>
        </div>
      </div>

      {/* Bottom right - Success metric */}
      <div
        className={`absolute bottom-[8%] right-[5%] w-[130px] sm:w-[160px] transition-all duration-700 hidden md:block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        style={{ transform: getTransform(0.9), transitionDelay: '700ms' }}
      >
        <div className="bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl shadow-lg shadow-purple-500/25 p-4 text-white">
          <CheckCircle2 className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-display font-bold">85%</p>
          <p className="text-xs text-purple-200">Funding success rate</p>
        </div>
      </div>

      {/* Middle right - Quick action */}
      <div
        className={`absolute top-[40%] right-[0%] w-[120px] sm:w-[140px] transition-all duration-700 hidden md:block ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        style={{ transform: getTransform(1.1), transitionDelay: '550ms' }}
      >
        <div className="bg-white rounded-xl shadow-lg shadow-gray-200/50 border border-gray-100 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-900">Quick Match</span>
            <ArrowUpRight className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <div className="flex -space-x-1.5">
            {['bg-purple-400', 'bg-violet-400', 'bg-fuchsia-400'].map((color, i) => (
              <div key={i} className={`w-6 h-6 rounded-full ${color} border-2 border-white`} />
            ))}
            <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs text-gray-500">
              +5
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 100),
      setTimeout(() => setStage(2), 300),
      setTimeout(() => setStage(3), 500),
      setTimeout(() => setStage(4), 700),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-[hsl(var(--warm-white))]">
      {/* Subtle background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-0 w-full h-full opacity-40"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, rgba(147, 51, 234, 0.06) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 80% 20%, rgba(251, 191, 36, 0.04) 0%, transparent 40%)
            `,
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      {/* Main content grid */}
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 min-h-screen grid lg:grid-cols-2 gap-8 lg:gap-4 items-center py-16 sm:py-20 lg:py-0">
        {/* Left: Content */}
        <div className="relative z-10 max-w-xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 mb-8 transition-all duration-700 ${stage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
                Invite Only
              </span>
            </span>
          </div>

          {/* Headline */}
          <div className="mb-6">
            <div className="overflow-hidden">
              <h1
                className={`font-display font-semibold text-gray-900 leading-[1.1] tracking-tight transition-all duration-700 ${stage >= 2 ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                  }`}
                style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
              >
                Structured Startup–Investor Matchmaking
              </h1>
            </div>
            <div className="overflow-hidden">
              <h1
                className={`font-display font-semibold leading-[1.1] tracking-tight transition-all duration-700 ${stage >= 2 ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                  }`}
                style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', transitionDelay: '100ms' }}
              >
                <span className="text-gradient-purple">Platform</span>
              </h1>
            </div>
          </div>

          {/* Description */}
          <p
            className={`text-lg lg:text-xl text-gray-600 mb-8 leading-relaxed transition-all duration-700 ${stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            Startups build readiness and gain visibility.
            Investors discover opportunities through structured signals - not cold pitches.
            Purple Venture Studio connects both through guided matchmaking.
          </p>

          {/* Feature pills */}
          <div
            className={`flex flex-wrap gap-3 mb-10 transition-all duration-700 ${stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            style={{ transitionDelay: '100ms' }}
          >
            {['Fundability Signals', 'Structured Discovery', 'Guided Interaction', 'Controlled Data Sharing'].map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-500" />
                {item}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div
            className={`flex flex-wrap gap-4 mb-20 transition-all duration-700 ${stage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <a href="#apply">
              <Button
                size="lg"
                className="btn-premium text-white px-8 py-6 text-base rounded-xl shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 transition-all group"
              >
                <span className="flex items-center gap-2">
                  Apply for Access
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </a>
            <a href="#how-it-works">
              <Button
                variant="outline"
                size="lg"
                className="px-6 py-6 text-base rounded-xl border-gray-200 hover:border-purple-200 hover:bg-purple-50/50 transition-all"
              >
                👉 Explore Matchmaking Flow
              </Button>
            </a>
          </div>

          {/* Trust stats */}
          <div
            className={`flex items-center gap-8 transition-all duration-700 ${stage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            style={{ transitionDelay: '100ms' }}
          >
            <div>
              <p className="text-2xl font-display font-bold text-gray-900">$50M+</p>
              <p className="text-sm text-gray-500">Deals facilitated</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <p className="text-2xl font-display font-bold text-gray-900">200+</p>
              <p className="text-sm text-gray-500">Founders</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div>
              <p className="text-2xl font-display font-bold text-gray-900">85%</p>
              <p className="text-sm text-gray-500">Success rate</p>
            </div>
          </div>
        </div>

        {/* Right: Platform showcase */}
        <div className="relative lg:h-screen flex items-center justify-center">
          <PlatformShowcase />
        </div>
      </div>
    </section>
  );
}
